import { ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { Button } from '@/components/ui/button';
import { api, isApiError } from '../../api';
import { Section } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { DomainLink } from '../../components/Ids';
import { KeyValue } from '../../components/KeyValue';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { CopyText, EmptyState, Loading } from '../../components/States';
import { formatDateTime } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { ResolveIssueButton } from '../operator/RegistryMarket';
import {
  EnumBadge,
  enumLabel,
  ISSUE_STATE,
  IssueTable,
  OutcomeBadge,
  PayloadBlock,
  RefreshButton,
  RegistryRef,
  SEVERITY,
  TransactionTable,
  useRegistryCrumbs,
  When,
} from './shared';

/** `/registry/reconciliation/:id`: one difference between our records and the registry (spec §29–§30). */
export function ReconciliationIssuePage() {
  const { t } = useTranslation('registry');
  const { id = '' } = useParams();
  const result = useApi((signal) => api.getReconciliationIssue(id, { signal }), [id]);
  const title = result.status === 'success' ? enumLabel('issueType', result.data.issue.issue_type) : t('issue.summary');
  useDocumentTitle(title);
  const crumbs = useRegistryCrumbs({ label: t('reconciliation.title'), to: '/registry/reconciliation' }, { label: title });

  if (result.status === 'loading') {
    return (
      <>
        <PageHeader title={t('issue.summary')} crumbs={crumbs} />
        <Loading rows={3} />
      </>
    );
  }
  if (result.status === 'error') {
    const notFound = isApiError(result.error) && result.error.status === 404;
    return (
      <>
        <PageHeader title={t('issue.summary')} crumbs={crumbs} />
        <DataError error={result.error} onRetry={notFound ? undefined : result.reload} title={notFound ? t('issue.notFound') : t('issue.loadError')} />
      </>
    );
  }

  const { issue, history, domain_transactions } = result.data;
  const tx = issue.registry_transaction_id;
  return (
    <>
      <PageHeader
        title={
          <span className="flex flex-wrap items-baseline gap-2">
            {enumLabel('issueType', issue.issue_type)}
            {issue.domain_name ? <DomainLink keyOrName={issue.domain_name} /> : null}
          </span>
        }
        crumbs={crumbs}
        subtitle={t('issue.subtitle')}
        actions={
          <>
            <EnumBadge group="severity" map={SEVERITY} value={issue.severity} />
            <EnumBadge group="issueState" map={ISSUE_STATE} value={issue.state} />
            <RefreshButton onClick={result.reload} />
            <ResolveIssueButton issue={issue} onChanged={result.reload} />
          </>
        }
      />
      <div className="grid gap-6 [&>*]:min-w-0 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <Section title={t('issue.summary')}>
          <KeyValue
            columns={2}
            rows={[
              [t('reconciliation.registry'), <RegistryRef key="r" name={issue.registry_name} code={issue.registry_code} />],
              [t('reconciliation.domain'), issue.domain_name ? <DomainLink key="d" keyOrName={issue.domain_name} /> : '—'],
              [t('reconciliation.type'), <span key="t" title={issue.issue_type}>{enumLabel('issueType', issue.issue_type)}</span>],
              [t('reconciliation.severity'), <EnumBadge key="s" group="severity" map={SEVERITY} value={issue.severity} />],
              [t('reconciliation.state'), <EnumBadge key="st" group="issueState" map={ISSUE_STATE} value={issue.state} />],
              [t('reconciliation.detected'), formatDateTime(issue.detected_at)],
              [t('reconciliation.resolution'), issue.resolution ?? '—'],
              [t('issue.resolvedAt'), issue.resolved_at ? formatDateTime(issue.resolved_at) : '—'],
              [t('issue.resolvedBy'), issue.resolved_by_name ?? issue.resolved_by ?? '—'],
              [t('issue.id'), <CopyText key="id" value={issue.id} />],
            ]}
          />
        </Section>

        <Section title={t('issue.command')}>
          {tx ? (
            <>
              <KeyValue
                rows={[
                  [t('reconciliation.transaction'), <code key="o" className="text-xs">{issue.transaction_operation ?? '—'}</code>],
                  [t('reconciliation.state'), <OutcomeBadge key="s" value={issue.transaction_state} />],
                  [
                    t('issue.trids'),
                    <span key="tr" className="font-mono text-xs">
                      {issue.transaction_cltrid ?? '—'}
                      <br />
                      <span className="text-muted-foreground">{issue.transaction_svtrid ?? '—'}</span>
                    </span>,
                  ],
                  [
                    t('issue.result'),
                    issue.transaction_result_code !== null ? (
                      <span key="rc">
                        <code className="text-xs">{issue.transaction_result_code}</code> {issue.transaction_result_message}
                      </span>
                    ) : (
                      '—'
                    ),
                  ],
                  [t('issue.errorClass'), issue.transaction_error_class ? <code key="e" className="text-xs">{issue.transaction_error_class}</code> : '—'],
                  [t('issue.started'), <When key="st" at={issue.transaction_started_at} />],
                  [t('issue.completed'), <When key="cp" at={issue.transaction_completed_at} />],
                ]}
              />
              <Button asChild variant="outline" size="sm" className="mt-4">
                <Link to={`/registry/transactions/${encodeURIComponent(tx)}`}>
                  {t('issue.openCommand')} <ArrowRight />
                </Link>
              </Button>
            </>
          ) : (
            <EmptyState title={t('issue.noCommand')} />
          )}
        </Section>

        <Section title={t('issue.compare')} className="xl:col-span-2">
          <p className="mb-3 text-xs text-muted-foreground">{t('issue.compareNote')}</p>
          <div className="grid gap-4 md:grid-cols-2 [&>*]:min-w-0">
            <PayloadBlock label={t('reconciliation.local')} value={issue.local_value} />
            <PayloadBlock label={t('reconciliation.remote')} value={issue.registry_value} />
          </div>
        </Section>

        <Section flush title={t('issue.history')} className="xl:col-span-2">
          <IssueTable rows={history} compact empty={<EmptyState title={t('issue.noHistory')} />} />
        </Section>

        <Section flush title={t('issue.commands')} className="xl:col-span-2">
          {domain_transactions.length === 0 ? (
            <div className="px-5 pb-5">
              <EmptyState title={t('issue.noCommands')} />
            </div>
          ) : (
            <TransactionTable rows={domain_transactions} showRegistry={false} />
          )}
        </Section>
      </div>
    </>
  );
}
